import { ref, watch, computed, onMounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import BetaScoreTracker from '@/services/scoreUsageTracker.js'
import {
  TrackerScope,
  TrackerSort,
  TrackerQuery,
  TrackerHash,
  TrackerTimeState
} from '@/enums/index.js'

export function useScoreUsageTracker(options = {}) {
  const {
    initialSort = TrackerSort.RECENT,
    limit = 20,
    mockYouItems = null,
    mockOrgItems = null
  } = options

  const route = useRoute()
  const router = useRouter()

  const loading = ref(false)
  const items = ref([])
  const sort = ref(initialSort)

  const pagination = ref({
    page: String(route.query[TrackerQuery.PAGE]) || '1',
    limit,
    pages: 1,
    total: 0
  })

  const hasYouItems = ref(false)
  const hasOrganizationItems = ref(false)
  const hasHistoryItems = ref(false)
  const normalizedHash = computed(() =>
    route.hash?.replace('#', '') ?? TrackerScope.YOU
  )

  const activeTab = computed(() => {
    switch (normalizedHash.value) {
      case TrackerTimeState.HISTORY:
        return TrackerTimeState.HISTORY

      case TrackerScope.ORGANIZATION:
        return TrackerScope.ORGANIZATION

      default:
        return TrackerScope.YOU
    }
  })

  const timeState = computed(() =>
    normalizedHash.value === TrackerTimeState.HISTORY
      ? TrackerTimeState.HISTORY
      : TrackerTimeState.ACTIVE
  )

  const getMock = (scope) =>
    scope === TrackerScope.YOU ? mockYouItems : mockOrgItems

  const setPagination = (page, total) => {
    pagination.value = {
      page,
      limit,
      total,
      pages: Math.max(1, Math.ceil(total / limit))
    }
  }

  const setInitialHash = async () => {
    if (route.hash) return

    await preloadScopes()

    let targetHash = TrackerHash.YOU

    if (hasYouItems.value) {
      targetHash = TrackerHash.YOU
    } else if (hasOrganizationItems.value) {
      targetHash = TrackerHash.ORGANIZATION
    } else if (hasHistoryItems.value) {
      targetHash = TrackerHash.HISTORY
    }

    router.replace({
      path: route.path,
      query: { ...route.query, [TrackerQuery.PAGE]: '1' },
      hash: targetHash
    })
  }

  async function preloadScopes() {
    for (const scope of [TrackerScope.YOU, TrackerScope.ORGANIZATION]) {
      const mock = getMock(scope)

      if (mock !== null) {
        scope === TrackerScope.YOU
          ? (hasYouItems.value = mock.length > 0)
          : (hasOrganizationItems.value = mock.length > 0)
        continue
      }

      const res = await BetaScoreTracker.load(
        scope,
        sort.value,
        1,
        limit,
        TrackerTimeState.ACTIVE
      )

      const hasItems = res.items?.length > 0

      scope === TrackerScope.YOU
        ? (hasYouItems.value = hasItems)
        : (hasOrganizationItems.value = hasItems)
    }

    const historyRes = await BetaScoreTracker.load(
      TrackerScope.YOU,
      sort.value,
      1,
      limit,
      TrackerTimeState.HISTORY
    )

    hasHistoryItems.value = historyRes.items?.length > 0
  }


  async function load(page) {
    loading.value = true

    const mock = getMock(activeTab.value)

    if (mock !== null) {
      items.value = mock
      setPagination(page, mock.length)
      loading.value = false
      return
    }

    try {
      const { ok, items: data, pagination: meta } =
        await BetaScoreTracker.load(
          activeTab.value,
          sort.value,
          page,
          limit,
          timeState.value
        )

      if (ok) {
        items.value = data
        pagination.value = meta
      } else {
        items.value = []
        setPagination(page, 0)
      }
    } catch (err) {
      console.error('BetaScoreTracker load failed', err)
      items.value = []
      setPagination(page, 0)
    }

    loading.value = false
  }

  function goToPage(page) {
    router.push({
      path: route.path,
      query: { ...route.query, [TrackerQuery.PAGE]: page },
      hash: route.hash || TrackerHash.YOU
    })
  }

  function toggleSort() {
    sort.value =
      sort.value === TrackerSort.RECENT
        ? TrackerSort.OLDEST
        : TrackerSort.RECENT

    goToPage('1')
  }

  onMounted(async () => {
    await setInitialHash()
  })

  watch(
    [() => route.query[TrackerQuery.PAGE], () => route.hash, () => sort.value],
    ([page]) => {
      if (!route.hash && !hasYouItems.value && !hasOrganizationItems.value) {
        return
      }

      const resolvedPage = Number(page) || 1
      pagination.value.page = resolvedPage
      load(resolvedPage)
    },
    { immediate: true }
  )

  return {
    loading,
    items,
    activeTab,
    sort,
    pagination,
    hasYouItems,
    hasOrganizationItems,
    hasHistoryItems,

    preloadScopes,
    load,
    toggleSort,
    goToPage
  }
}
